import React from 'react'
import { observer } from 'mobx-react'
import {
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheet,
  ModalSheetHeaderCloseButton,
  ModalSheetHeaderShell,
  ModalSheetContent,
} from 'components/ModalSheet'
import Button from 'components/Button'
import { useDropzone } from 'hooks/use-dropzone'
import FileButton from 'components/FileButton'
import { DocumentCreationItem } from 'documents/views/DocumentCreationModal/DocumentCreationItem'
import JobProgressionRequestDraftApprovalViewModel from './JobProgressionRequestDraftApprovalViewModel'
import styles from './JobProgressionRequestDraftApprovalModal.sass'

interface Props {
  vm: JobProgressionRequestDraftApprovalViewModel
}

export default observer(function JobProgressionRequestDraftApprovalModal({
  vm,
}: Props) {
  const documentViewModel = vm.documentViewModel
  function onDrop(files: FileList) {
    documentViewModel?.addFiles(files)
  }

  function onEnter() {
    documentViewModel?.setDragging()
  }

  function onLeave() {
    documentViewModel?.stopDragging()
  }

  const [, dropZoneRef] = useDropzone<HTMLDivElement>(onDrop, onEnter, onLeave)

  if (!documentViewModel) {
    return
  }

  return (
    <ModalSheet
      ref={dropZoneRef}
      showing={documentViewModel.showing}
      disableCloseOnOutsideClick={true}
      onRequestClose={vm.close}
    >
      <ModalSheetHeaderShell size="auto" bordered>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerInfo}>
              <div className={styles.headerTitle}>
                <span>Upload draft documents</span>
              </div>
              <div className={styles.headerDescription}>
                <span>Upload the draft return for your client to review</span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.closeBtn}>
          <ModalSheetHeaderCloseButton onClick={vm.close} />
        </div>
      </ModalSheetHeaderShell>
      <ModalSheetContent>
        <div className={styles.creationItemList}>
          {documentViewModel.documentCreationItemVMs.map(
            (docCreationItemVM, index) => (
              <div key={index}>
                <DocumentCreationItem
                  index={index}
                  vm={docCreationItemVM}
                  removeFile={documentViewModel.removeFile}
                />
              </div>
            ),
          )}
          <div
            className={
              documentViewModel.dragging
                ? styles.dropZoneItemDragging
                : styles.dropZoneItem
            }
          >
            <span className={styles.dropZoneTitle}>
              Drag and drop files here or&nbsp;
              <FileButton
                multiple
                onChange={documentViewModel.addFiles}
                data-test="upload-draft-documents-file-input"
              >
                <Button
                  className={styles.dropZoneButton}
                  borderless
                  type="text"
                >
                  Upload another file
                </Button>
              </FileButton>
            </span>
          </div>
        </div>
      </ModalSheetContent>
      <ModalSheetFooter>
        <div className={styles.footer}>
          <div className={styles.uploadButton}>
            <ModalSheetFooterButtonGroup>
              <Button
                type="primary"
                dataTest="upload-draft-documents-confirm-button"
                disabled={
                  vm.requestDraftApproval.pending || !vm.canRequestDraftApproval
                }
                onClick={() => vm.requestDraftApproval()}
              >
                Upload Documents
              </Button>
            </ModalSheetFooterButtonGroup>
          </div>
        </div>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
